import React, {
 useState, useEffect
} from 'react';
import {
 useParams
} from 'react-router-dom';
import {
 Button
} from 'shared/components/ui/button';
import {
 Input
} from 'shared/components/ui/input';
import {
 Label
} from 'shared/components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from 'shared/components/ui/select';
import {
 Card, CardContent, CardHeader, CardTitle
} from 'shared/components/ui/card';
import {
 ArrowLeft, Download, Loader2
} from 'lucide-react';
import {
 connectionService
} from 'shared/api/services/BEX';
import {
 SchemaColumnDataModel
} from 'shared/api/services/BEXO/Types/Bexo.types';

interface ImportFromDataSourceProps {
    onBack: () => void;
    onResult: (tableName: string, columns: SchemaColumnDataModel[]) => void;
}

interface ConnectionOption {
    id: string;
    name: string;
    type: string;
}

function makeColumn(
    columnName: string,
    dataType: string,
    constraints: string[] = [],
    length = 255
): SchemaColumnDataModel {
    return {
        columnName,
        dataType,
        length: dataType === 'string' ? length : 0,
        fieldsAccessBy: [],
        description: '',
        readOnly: false,
        visibleStatus: true,
        format: '',
        default: '',
        constraints,
        references: {
 type: '',
table: '',
column: '',
values: []
},
        validation: [],
    };
}

function mockImportColumns(): SchemaColumnDataModel[] {
    return [
        makeColumn('id', 'integer', ['NOT NULL', 'UNIQUE']),
        makeColumn('name', 'string', ['NOT NULL'], 200),
        makeColumn('description', 'string', [], 500),
        makeColumn('status', 'string', [], 30),
        makeColumn('created_at', 'datetime', []),
        makeColumn('updated_at', 'datetime', []),
    ];
}

const ImportFromDataSource: React.FC<ImportFromDataSourceProps> = ({
 onBack, onResult
}) => {
    const {
 appId, oragID
} = useParams<{ appId: string; oragID: string }>();
    const [connections, setConnections] = useState<ConnectionOption[]>([]);
    const [selectedConnection, setSelectedConnection] = useState('');
    const [tableName, setTableName] = useState('');
    const [isImporting, setIsImporting] = useState(false);
    const [isLoadingConnections, setIsLoadingConnections] = useState(true);

    useEffect(() => {
        const loadConnections = async () => {
            if (!oragID || !appId) return;
            try {
                const data = await connectionService.getConnections(oragID, appId);
                if (Array.isArray(data)) {
                    setConnections(data.map((c: any) => ({
                        id: c.connection_id,
                        name: c.connection_name,
                        type: c.source_type,
                    })));
                }
            } catch (error) {
                console.error('Error loading connections:', error);
            } finally {
                setIsLoadingConnections(false);
            }
        };
        loadConnections();
    }, [oragID, appId]);

    const handleImport = () => {
        if (!selectedConnection || !tableName.trim()) return;
        setIsImporting(true);
        // Mock import - in production this would call backend introspection
        setTimeout(() => {
            const columns = mockImportColumns();
            setIsImporting(false);
            onResult(tableName.trim(), columns);
        }, 2000);
    };

    return (
        <div className="flex flex-col gap-8 max-w-2xl mx-auto py-8">
            <div>
                <Button variant="ghost" size="sm" onClick={onBack} className="mb-4">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                </Button>
                <div className="flex items-center gap-2">
                    <Download className="h-6 w-6" />
                    <h2 className="text-2xl font-bold">Import from Data Source</h2>
                </div>
                <p className="text-muted-foreground mt-1">
                    Connect to an existing database or API and import the table structure.
                </p>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="text-lg">Select Source</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="space-y-2">
                        <Label>Connection</Label>
                        {isLoadingConnections ? (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Loading connections...
                            </div>
                        ) : connections.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                                No connections available. Please create a connection first.
                            </p>
                        ) : (
                            <Select value={selectedConnection} onValueChange={setSelectedConnection}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select a connection" />
                                </SelectTrigger>
                                <SelectContent>
                                    {connections.map((conn) => (
                                        <SelectItem key={conn.id} value={conn.id}>
                                            {conn.name} ({conn.type})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label>Table / Entity Name</Label>
                        <Input
                            placeholder="e.g., users, products, orders"
                            value={tableName}
                            onChange={(e) => setTableName(e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground">
                            Enter the name of the table or collection to import from.
                        </p>
                    </div>

                    <Button
                        onClick={handleImport}
                        disabled={!selectedConnection || !tableName.trim() || isImporting}
                        className="w-full"
                    >
                        {isImporting ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Importing structure...
                            </>
                        ) : (
                            <>
                                <Download className="mr-2 h-4 w-4" />
                                Import Schema
                            </>
                        )}
                    </Button>
                </CardContent>
            </Card>
        </div>
    );
};

export default ImportFromDataSource;
